import { useState, useCallback, useEffect, useRef } from "react";

import { setLoadingPref, TILE_KINDS } from "../utils/loadingPreferences";

/**
 * How long a piece of content is held on screen once it lands. The loading
 * tile carries memories from the user's own journal, quotes and milestones —
 * it is a content moment, not just a spinner, so the content is guaranteed a
 * readable dwell rather than vanishing the instant data arrives.
 *
 * Both values are deliberately tunable from one place. If the dwell proves
 * too long in daily use, change it here.
 */
const DWELL_MS = 4000;
const DWELL_MS_MILESTONE = 6000;
const TICK_MS = 100;

/* How long the "turned off" confirmation holds. Long enough to read where the
   switch went and reach for Undo; short enough that it is not a second
   interruption in place of the one just dismissed. */
const CONFIRM_MS = 2200;

/* The longer dwell serves the earned celebrations and the one tile that asks
   something of the reader: a sentence, an instruction and a link to aim at do
   not fit in four seconds. Hold extends it, Skip ends it. */
const dwellFor = (tileType) =>
  tileType === "milestone" ||
  tileType === "welcome" ||
  tileType === "backlog"
    ? DWELL_MS_MILESTONE
    : DWELL_MS;

/**
 * Manages the loading tile's visibility as a content moment.
 *
 * @param {boolean} externalLoading - the screen's own loading flag.
 * @param {object}  content - the tile content from useLoadingContent().
 * @returns {object}
 *  - isShowing: whether the tile should render.
 *  - startLoading / stopLoading: imperative control, for screens that need it.
 *  - hoverEvents: legacy mouse-only handlers, retained for compatibility.
 *  - tileProps: everything LoadingTile needs — spread it onto the component.
 */
export const usePersistentLoading = (
  externalLoading = false,
  content = null,
) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  // Which kind was just switched off, while the confirmation is showing.
  const [justDisabled, setJustDisabled] = useState(null);

  // null = the dwell has not begun. 0 = it has finished (or was skipped).
  const [remaining, setRemaining] = useState(null);
  const [dwellTotal, setDwellTotal] = useState(0);

  const startLoading = useCallback(() => setIsLoading(true), []);
  const stopLoading = useCallback(() => setIsLoading(false), []);

  const busy = Boolean(externalLoading) || isLoading;

  const tileType = content?.tileType;
  const contentReady = Boolean(tileType) && tileType !== "loading";

  /* The moment begins the first time the screen is busy. Once begun it runs to
     completion, so a load that finishes in 80ms cannot make the tile flash out
     and then reappear when the content lands a moment later. */
  const hasStarted = useRef(busy);
  if (busy && !hasStarted.current) hasStarted.current = true;

  /* A fresh load starts a fresh moment. */
  const wasBusy = useRef(busy);
  useEffect(() => {
    if (busy && !wasBusy.current) {
      setRemaining(null);
      setDwellTotal(0);
      setIsPaused(false);
      setJustDisabled(null);
    }
    wasBusy.current = busy;
  }, [busy]);

  /* Start the countdown when real content lands — not when loading ends. */
  useEffect(() => {
    if (!hasStarted.current || !contentReady || remaining !== null) return;
    const total = dwellFor(tileType);
    setDwellTotal(total);
    setRemaining(total);
  }, [contentReady, tileType, remaining]);

  /* Tick down, unless held. */
  useEffect(() => {
    if (remaining === null || remaining <= 0 || isPaused) return;
    const id = setInterval(() => {
      setRemaining((r) => (r === null ? null : Math.max(0, r - TICK_MS)));
    }, TICK_MS);
    return () => clearInterval(id);
  }, [remaining, isPaused]);

  const pause = useCallback(() => setIsPaused(true), []);
  const resume = useCallback(() => setIsPaused(false), []);
  const toggleHold = useCallback(() => setIsPaused((held) => !held), []);

  /* Skip ends the dwell. It cannot skip past an actual in-flight load — if the
     screen is still busy there is nothing else to show yet. */
  const skip = useCallback(() => {
    setRemaining((r) => (r === null ? null : 0));
    setIsPaused(false);
  }, []);

  /* Turning a tile off happens from the tile, in the moment it is unwanted —
     that is the whole point, so it cannot require finding a settings page
     first. It does not vanish on the click: the dwell is cut to a short
     confirmation that says where the switch now lives and offers an undo.

     Only quotes and memories are in TILE_KINDS. A milestone has no off
     switch, so `disableKind` is undefined and no button is offered. */
  const disableKind = TILE_KINDS[tileType];

  const disable = useCallback(() => {
    if (!disableKind) return;
    setLoadingPref(disableKind, false);
    setJustDisabled(disableKind);
    setIsPaused(false);
    setDwellTotal(CONFIRM_MS);
    setRemaining(CONFIRM_MS);
  }, [disableKind]);

  /* Undo puts the switch back and returns the reader to the content they were
     looking at, with a fresh dwell — the click was a mistake, so the moment
     should carry on rather than end. */
  const undo = useCallback(() => {
    if (!justDisabled) return;
    setLoadingPref(justDisabled, true);
    setJustDisabled(null);
    const total = dwellFor(tileType);
    setDwellTotal(total);
    setRemaining(total);
  }, [justDisabled, tileType]);

  const dwelling = remaining !== null && remaining > 0;
  const isShowing = hasStarted.current && (busy || dwelling);

  const hoverEvents = {
    onMouseEnter: pause,
    onMouseLeave: resume,
  };

  const tileProps = {
    onMouseEnter: pause,
    onMouseLeave: resume,
    onFocus: pause,
    onBlur: resume,
    onTouchStart: pause,
    isHeld: isPaused,
    onHold: toggleHold,
    onSkip: skip,
    canSkip: dwelling,
    progress:
      dwellTotal > 0 && remaining !== null ? remaining / dwellTotal : null,
    onDisable: disableKind ? disable : undefined,
    justDisabled,
    onUndo: justDisabled ? undo : undefined,
  };

  return { isShowing, startLoading, stopLoading, hoverEvents, tileProps };
};
